import { useRef } from 'react';
import type { TReminderTemplate } from '@src/shared/types/reminder-template';
import { Button } from '@src/design-system/button';
import { ErrorMessage, Form, Formik } from 'formik';
import { object, string } from 'yup';
import { CustomErrorMessage } from '@src/design-system/error-message';
import { PhoneNumberInput } from '@src/design-system/phone-number-input';
import { useTestReminderWhatsapp } from '@src/features/reminders/services/test-reminder';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { DialogClose, DialogFooter } from '@src/design-system/dialog';
import { showToast } from '@src/shared/utils/helpers/show-toast';

export const TestWhatApp = ({
  reminderTemplate,
}: {
  reminderTemplate: TReminderTemplate;
}) => {
  const closeRef = useRef<HTMLButtonElement>(null);
  const { mutate: testReminderWhatsapp, isPending } = useTestReminderWhatsapp();

  const handleSubmit = async (values: { phone_number: string }) => {
    testReminderWhatsapp(
      {
        phone_number: values.phone_number,
        template: reminderTemplate.id,
      },
      {
        onSuccess: () => {
          showToast({
            title: 'Sent Successfully',
            description: 'Your test message has been sent successfully.',
            variant: 'success',
          });
          closeRef.current?.click();
        },
      }
    );
  };

  return (
    <Formik
      initialValues={{ phone_number: '' }}
      validationSchema={object().shape({
        phone_number: string()
          .required('Phone number is required')
          .test(
            'is-valid-phone',
            'Phone number is invalid',
            (value) => !value || isValidPhoneNumber(value)
          ),
      })}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue }) => {
        return (
          <Form className='flex min-h-full flex-col'>
            <div className='flex-1'>
              <PhoneNumberInput
                label='Reminder Test'
                helperText='Enter your number to receive a test message.'
                name='phone_number'
                placeholder='ex: +20124356762'
                onChange={(value) => {
                  setFieldValue('phone_number', value);
                }}
                value={values.phone_number}
              />

              <ErrorMessage
                name='phone_number'
                component={CustomErrorMessage}
              />
            </div>

            <DialogFooter className='mt-auto shrink-0 bg-white p-0 pt-4 grid grid-cols-2 gap-2'>
              <Button
                type='submit'
                disabled={isPending}
              >
                Send Test Message
              </Button>
              <DialogClose
                ref={closeRef}
                asChild
              >
                <Button variant='primaryOutline'>Cancel</Button>
              </DialogClose>
            </DialogFooter>
          </Form>
        );
      }}
    </Formik>
  );
};
